import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnInit,
  Signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BarRatingModule } from 'ngx-bar-rating';
import { IonicModule } from '@ionic/angular';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { exhaustMap, Observable, of, take, tap } from 'rxjs';
import { Router } from '@angular/router';
import { AppRoutes, PLATFORM_TOKEN, PlatformProvider, PreventMultiClickDirective } from '@core';
import { InAppReview } from '@capacitor-community/in-app-review';
import { RateAppCommandHandler, RateAppCommandPayload } from '../../application/handlers';

@Component({
  selector: 'lib-rating-app',
  templateUrl: './rating-app.component.html',
  styleUrls: ['./rating-app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [BarRatingModule, IonicModule, ReactiveFormsModule, PreventMultiClickDirective]
})
export class RatingAppComponent implements OnInit {
  private readonly rateApplicationCommandHandler: RateAppCommandHandler = inject(RateAppCommandHandler);
  private readonly router: Router = inject(Router);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);

  readonly gameId: InputSignal<string> = input.required<string>();
  readonly isHostedGame: InputSignal<boolean> = input.required<boolean>();

  readonly ratingForm: FormGroup = new FormGroup({
    comment: new FormControl(),
    gameId: new FormControl(),
    appRate: new FormControl(0),
    hostRate: new FormControl(0)
  });

  private readonly appRateValue: Signal<number | undefined> = toSignal(
    this.ratingForm.get('appRate')!.valueChanges as Observable<number>
  );

  private readonly hostRateValue: Signal<number | undefined> = toSignal(
    this.ratingForm.get('hostRate')!.valueChanges as Observable<number>
  );

  readonly isIos: boolean = this.platformProvider.isiOS;

  readonly showCommentLabel: Signal<boolean> = computed(() => {
    const appRate: number = this.appRateValue() ?? 0;
    return appRate < 4 && appRate !== 0;
  });

  readonly ratingNotSet: Signal<boolean> = computed(() => {
    const appRate: number = this.appRateValue() ?? 0;
    const hostRate: number = this.hostRateValue() ?? 0;
    return appRate === 0 || (hostRate === 0 && !this.isHostedGame());
  });

  ngOnInit(): void {
    this.ratingForm.patchValue({ gameId: this.gameId() });
  }

  changeAppRate(event: number): void {
    this.ratingForm.patchValue({ appRate: event });
  }

  changeHostRate(event: number): void {
    this.ratingForm.patchValue({ hostRate: event });
  }

  sendRate(): void {
    if (this.ratingNotSet()) return;

    const payload: RateAppCommandPayload = {
      gameId: this.ratingForm.get('gameId')?.value,
      appRate: this.ratingForm.get('appRate')?.value,
      hostRate: this.isHostedGame() ? null : this.ratingForm.get('hostRate')?.value,
      comment: this.ratingForm.get('comment')?.value
    };

    of(payload)
      .pipe(
        exhaustMap((payload: RateAppCommandPayload) =>
          this.rateApplicationCommandHandler.rateApp(payload).pipe(
            take(1),
            tap(() => this.rateInStore(payload.appRate))
          )
        )
      )
      .subscribe(() => this.router.navigate([AppRoutes.FIND_GAMES]));
  }

  private rateInStore(appRate: number | null): void {
    if (!this.platformProvider.isNativeRun || (appRate || 0) < 5) return;

    InAppReview.requestReview();
  }
}
